import type { AuthUser, JwtPayload } from "./types";

function parseAllowedDomains(raw?: string): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);
}

function extractDomain(payload: JwtPayload): string | null {
  if (payload.hd) return payload.hd.toLowerCase();
  const email = payload.email;
  if (!email) return null;
  const at = email.lastIndexOf("@");
  if (at < 0) return null;
  return email.slice(at + 1).toLowerCase();
}

export function validateDomain(
  payload: JwtPayload,
  allowedDomains?: string,
): boolean {
  const allowed = parseAllowedDomains(allowedDomains);
  if (allowed.length === 0) return true;
  const domain = extractDomain(payload);
  if (!domain) return false;
  return allowed.includes(domain);
}

export function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const raw = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const base64 = raw.padEnd(raw.length + ((4 - (raw.length % 4)) % 4), "=");
    const binary = atob(base64);
    const json = new TextDecoder().decode(
      Uint8Array.from(binary, (c) => c.charCodeAt(0)),
    );
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const ms = tokenExpiresInMs(token);
  return ms === null || ms <= 0;
}

export function tokenExpiresInMs(token: string): number | null {
  const payload = decodeJwt(token);
  if (!payload) return null;
  if (typeof payload.exp !== "number" || !Number.isFinite(payload.exp))
    return null;
  return payload.exp * 1000 - Date.now();
}

export function getUserFromToken(payload: JwtPayload): AuthUser | null {
  if (!payload.sub) return null;

  const firstName = payload.given_name ?? "";
  const lastName = payload.family_name ?? "";
  const username = payload.preferred_username ?? payload.email ?? payload.sub;
  const name =
    firstName || lastName ? `${firstName} ${lastName}`.trim() : username;

  return {
    id: payload.sub,
    email: payload.email,
    name,
    firstName,
    lastName,
    username,
  };
}
